"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  ColorSwatches,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import { MindMapDiagram } from "@/components/tool-kit/diagrams/mind-map-diagram";
import {
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { type ChartColorId, colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { DIAGRAM_COLORS, diagramColor } from "@/lib/tool-kit/diagrams/color";
import {
  layoutMindMap,
  type MindMapLayoutKind,
  type OutlineNode,
  outlineText,
  parseOutline,
} from "@/lib/tool-kit/diagrams/mind-map";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const MIND_MAP_MAKER_STRINGS = {
  outlineLabel:
    "Your outline — one idea per line, indented under the idea it belongs to",
  outlineHint:
    "The first line is the central idea. Indent with spaces or tabs; bullets and numbering (-, *, 1., a), I.) are removed. Each line hangs under the nearest line above it that is indented less.",
  exampleOutline:
    "Social media and teenagers\n  Benefits\n    Staying in touch with friends\n    Finding support communities\n    Creative expression\n  Risks\n    Cyberbullying\n    Sleep lost to late-night scrolling\n    Comparing yourself with others\n  What schools can do\n    Teach digital literacy\n    Agree on phone-free times\n  Evidence to find\n    Surveys of teenagers\n    Studies on sleep and screens",
  exampleTitle: "Essay Plan: Social Media and Teenagers",
  ideasDetected: "one:{count} idea|other:{count} ideas",
  branchesDetected: "one:{count} main branch|other:{count} main branches",
  layoutLabel: "Layout",
  layoutBoth: "Map: branches on both sides",
  layoutTree: "Tree: left to right",
  layoutRadial: "Radial: rings around the center",
  colorsLabel: "Branch colors",
  colorsBranch: "A color for each branch",
  colorsOne: "One color for all",
  firstColor: "Color of the first branch",
  oneColor: "Branch color",
  colorLabel: "Color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  enterOutline: "Type at least one line to start the map.",
  onlyRoot:
    "Add ideas on the lines below the first one, indented, to grow branches.",
  dropped:
    "one:{count} line was left out — a map holds up to {limit} ideas.|other:{count} lines were left out — a map holds up to {limit} ideas.",
  repeatedColors:
    "There are more main branches than colors ({count}), so colors repeat. Group related branches under fewer headings, or use one color.",
  levelColumn: "Level",
  ideaColumn: "Idea",
  parentColumn: "Under",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy outline",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the map as a table",
  chartLabel: "Mind map",
  chartDescription: "Mind map of “{root}” with {branches}: {list}.",
} as const;

const LIMIT = 120;

/** Every idea in outline order: level, words, and the idea it hangs under. */
function outlineRows(
  root: OutlineNode,
  fmt: (value: number) => string
): string[][] {
  const rows: string[][] = [];
  const walk = (node: OutlineNode, depth: number, parent: string) => {
    rows.push([fmt(depth), node.text, parent]);
    for (const child of node.children) {
      walk(child, depth + 1, node.text);
    }
  };
  walk(root, 0, "");
  return rows;
}

export default function MindMapMakerWidget() {
  const strings = useToolUi(MIND_MAP_MAKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState<string>(strings.exampleOutline);
  const [kind, setKind] = useState<MindMapLayoutKind>("both");
  const [oneColor, setOneColor] = useState(false);
  const [colorId, setColorId] = useState<ChartColorId>("blue");
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const outline = parseOutline(raw, LIMIT);
  const root = outline.root;
  const layout = root
    ? layoutMindMap(root, kind, style.apa ? "" : style.title)
    : null;
  const branches = root?.children ?? [];
  const start = colorIndex(colorId);
  const branchColors = branches.map((_, index) =>
    oneColor ? CHART_COLORS[start] : diagramColor(start, index)
  );
  const head = [strings.levelColumn, strings.ideaColumn, strings.parentColumn];
  const rows = root ? outlineRows(root, fmt) : [];
  const description = root
    ? t(strings.chartDescription, {
        root: root.text,
        branches: tPlural(strings.branchesDetected, locale, branches.length, {
          count: fmt(branches.length),
        }),
        list: branches.map((branch) => branch.text).join("; "),
      })
    : "";

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.outlineHint}
        id="mmm-outline"
        label={strings.outlineLabel}
        onChange={setRaw}
        prose
        rows={12}
        value={raw}
      />
      <div aria-live="polite" className="space-y-2">
        {root ? (
          <p className={HINT}>
            {tPlural(strings.ideasDetected, locale, outline.count, {
              count: fmt(outline.count),
            })}
            {" · "}
            {tPlural(strings.branchesDetected, locale, branches.length, {
              count: fmt(branches.length),
            })}
          </p>
        ) : (
          <Notice>{strings.enterOutline}</Notice>
        )}
        {root && branches.length === 0 ? (
          <Notice>{strings.onlyRoot}</Notice>
        ) : null}
        {outline.dropped > 0 ? (
          <Notice>
            {tPlural(strings.dropped, locale, outline.dropped, {
              count: fmt(outline.dropped),
              limit: fmt(LIMIT),
            })}
          </Notice>
        ) : null}
        {!oneColor && branches.length > DIAGRAM_COLORS.length ? (
          <Notice>
            {t(strings.repeatedColors, { count: fmt(DIAGRAM_COLORS.length) })}
          </Notice>
        ) : null}
      </div>
      <PillGroup
        label={strings.layoutLabel}
        name="mmm-layout"
        onChange={setKind}
        options={[
          { value: "both", label: strings.layoutBoth },
          { value: "tree", label: strings.layoutTree },
          { value: "radial", label: strings.layoutRadial },
        ]}
        value={kind}
      />
      {root && layout ? (
        <ChartFigure
          apa={style.apa}
          data={outlineText(root)}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "mind-map")}
          labels={strings}
          note={style.note}
          table={{ head, rows }}
          title={style.title}
        >
          <MindMapDiagram
            branchColors={branchColors}
            description={description}
            id="mmm-map"
            label={style.title.trim() || strings.chartLabel}
            layout={layout}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        idPrefix="mmm"
        labels={strings}
        style={style}
        update={update}
      >
        <PillGroup
          label={strings.colorsLabel}
          name="mmm-colors"
          onChange={(value) => setOneColor(value === "one")}
          options={[
            { value: "branch", label: strings.colorsBranch },
            { value: "one", label: strings.colorsOne },
          ]}
          value={oneColor ? "one" : "branch"}
        />
        <ColorSwatches
          labels={strings}
          legend={oneColor ? strings.oneColor : strings.firstColor}
          name="mmm-color"
          onChange={setColorId}
          value={colorId}
        />
      </ChartStyleFields>
    </div>
  );
}
